import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useAuditLog,
  useMyMfaState,
  useVerifyAuditChain,
  useChainCheckHistory,
  type AuditLogRow,
} from '@/data/hardening';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAudit,
});

function useAal() {
  return useQuery({
    queryKey: ['aal'],
    queryFn: async (): Promise<'aal1' | 'aal2'> => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      return (data.currentLevel as 'aal1' | 'aal2' | null) ?? 'aal1';
    },
  });
}

function AdminAudit() {
  const [resourceType, setResourceType] = useState('');
  const [action, setAction] = useState('');
  const [actorRole, setActorRole] = useState('');
  const [days, setDays] = useState(7);
  const aal = useAal();
  const mfaState = useMyMfaState();

  // Memoised so the value is stable across renders: `since` feeds the
  // useAuditLog query key, and recomputing Date.now() every render would
  // churn the key and trigger an infinite refetch loop.
  const since = useMemo(() => new Date(Date.now() - days * 86400 * 1000).toISOString(), [days]);
  const log = useAuditLog({
    resourceType: resourceType || undefined,
    action: action || undefined,
    actorRole: actorRole || undefined,
    since,
  });
  const verify = useVerifyAuditChain();
  const history = useChainCheckHistory();
  const lastEmailMfaAt = mfaState.data?.last_email_mfa_at ?? null;
  const emailMfaCurrent =
    lastEmailMfaAt != null &&
    Date.now() - new Date(lastEmailMfaAt).getTime() < 12 * 60 * 60 * 1000;
  const adminMfaCurrent = aal.data === 'aal2' || emailMfaCurrent;

  async function runVerification() {
    try {
      const r = await verify.mutateAsync();
      toast({
        title: r.is_ok ? 'Chain OK' : 'Chain BROKEN',
        description: `${r.rows_checked} rows checked${
          r.first_bad_row_id ? `; first bad: ${r.first_bad_row_id}` : ''
        }`,
        variant: r.is_ok ? 'success' : 'error',
      });
    } catch (err) {
      toast({
        title: "Couldn't verify",
        description: err instanceof Error ? err.message : 'Requires aal2 (MFA)',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Operations
          </p>
          <h1 className="mt-2">Audit log</h1>
          <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
            Tamper-evident record of every administrative write. Each row's hash chains to the
            previous one — the verification button below re-walks the chain and flags any tamper.
          </p>
        </div>
        <Button onClick={() => void runVerification()} disabled={verify.isPending}>
          {verify.isPending ? 'Verifying…' : 'Verify chain'}
        </Button>
      </header>

      {history.data && history.data.length > 0 && (
        <section aria-labelledby="chain-history" className="surface p-4">
          <h2
            id="chain-history"
            className="font-medium tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase"
          >
            Recent chain checks
          </h2>
          <ul className="mt-2 grid gap-1 text-[var(--text-sm)]">
            {history.data.slice(0, 5).map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3">
                <span className="text-[var(--color-ink-muted)] tabular-nums">
                  {new Date(c.ran_at).toLocaleString()}
                </span>
                <span className="tabular-nums">{c.rows_checked} rows</span>
                <Badge tone={c.is_ok ? 'success' : 'danger'}>{c.is_ok ? 'OK' : 'BROKEN'}</Badge>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="filters" className="surface-elevated grid gap-3 p-4 sm:grid-cols-4">
        <h2 id="filters" className="sr-only">
          Filters
        </h2>
        <div>
          <Label htmlFor="f-rt">Resource type</Label>
          <Input
            id="f-rt"
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            placeholder="charges"
          />
        </div>
        <div>
          <Label htmlFor="f-act">Action contains</Label>
          <Input
            id="f-act"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="update"
          />
        </div>
        <div>
          <Label htmlFor="f-role">Actor role</Label>
          <Input
            id="f-role"
            value={actorRole}
            onChange={(e) => setActorRole(e.target.value)}
            placeholder="admin"
          />
        </div>
        <div>
          <Label htmlFor="f-days">Days back</Label>
          <Input
            id="f-days"
            type="number"
            min="1"
            max="365"
            value={days}
            onChange={(e) => setDays(Number.parseInt(e.target.value, 10) || 7)}
          />
        </div>
      </section>

      <section aria-labelledby="log-heading">
        <h2 id="log-heading" className="text-[var(--text-xl)]">
          {log.data?.length ?? 0} entries
        </h2>
        {log.isLoading && <p aria-live="polite">Loading…</p>}
        {log.isError && (
          <p role="alert" className="text-[var(--color-danger)]">
            Couldn't load audit log. Confirm your email MFA check is current.
          </p>
        )}
        {!log.isLoading && (log.data?.length ?? 0) === 0 && adminMfaCurrent && (
          <Empty title="No entries" description="No audit rows match these filters." />
        )}
        {!log.isLoading && (log.data?.length ?? 0) === 0 && !adminMfaCurrent && (
          <Empty
            title="MFA session expired"
            description="The audit log requires a current email MFA check. Open Security to send and verify a code."
            action={
              <Link to="/admin/security">
                <Button variant="secondary">Open Security</Button>
              </Link>
            }
          />
        )}
        <ul className="mt-4 grid gap-2">
          {(log.data ?? []).map((r) => (
            <AuditRow key={r.id} row={r} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function AuditRow({ row }: { row: AuditLogRow }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="surface grid gap-2 p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="font-medium text-[var(--text-sm)] tabular-nums">
            {row.action}
            <span className="text-[var(--color-ink-muted)]">
              {' · '}
              {row.resource_type}
            </span>
            {row.resource_id && (
              <span className="text-[var(--color-ink-muted)]"> ({row.resource_id})</span>
            )}
          </p>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)] tabular-nums">
            {new Date(row.occurred_at).toLocaleString()} · {row.actor_role ?? '—'}
            {row.actor_user_id && <> · {row.actor_user_id.slice(0, 8)}</>}
          </p>
        </div>
        <button
          type="button"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] underline hover:text-[var(--color-ink)]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide' : 'Details'}
        </button>
      </header>
      {open && (
        <div className="grid gap-2 text-[var(--text-xs)]">
          <pre className="overflow-x-auto rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)] p-2 tabular-nums">
            {JSON.stringify({ before: row.before_state, after: row.after_state }, null, 2)}
          </pre>
          <p className="text-[var(--color-ink-subtle)] tabular-nums">
            hash {row.row_hash.slice(0, 16)}… · prev {row.prev_hash?.slice(0, 16) ?? '(genesis)'}…
          </p>
        </div>
      )}
    </li>
  );
}
